// Types and small helpers for Receiving (manifest processing).

export type Outcome = 'retail' | 'auction' | 'garbage';

/** A scanned manifest PDF being read on the server (see server/services/manifestOcr/process.ts). */
export interface ManifestImport {
  id: string;
  fileName: string;
  status: 'reading' | 'ready' | 'failed' | 'created';
  pagesTotal: number | null;
  pagesDone: number;
  error: string | null;
  createdAt: string;
  byName?: string;
}
export interface ReadItem {
  item: string;
  description: string;
  sell: string;
  qty: number;
  totalSalvage: string;
  pages: number[];
  status: 'verified' | 'numbers_ok' | 'check';
  problems: string[];
}
export interface ReadReceipt {
  receipt: string;
  date: string | null;
  pagesRead: number;
  lastPageFound: boolean;
  missingPages: number[];
  lines: number;
  linesToCheck: number;
  unreadableLines: string[];
  totalSellPrinted: string | null;
  /** Lines that were read and add up (lines to fix are not included). */
  totalSellFromLines: string;
  totalSellMatches: boolean | null;
  items: ReadItem[];
  exists: boolean;
  fixes: FixLine[];
  salvageRates: number[];
}
export type FixResolution =
  | { action: 'fixed'; item: string; description: string | null; qty: number; sell: string; salvagePct: number; by: string; at: string }
  | { action: 'skipped'; by: string; at: string };
export interface FixLine {
  key: string;
  kind: 'check' | 'unreadable';
  page: number;
  raw: string;
  problems: string[];
  suggested: { item: string | null; description: string | null; qty: string | null; sell: string | null; salvagePct: string | null };
  cropKey?: string | null;
  resolution?: FixResolution | null;
}
export interface ManifestReading {
  pages: number;
  receipts: ReadReceipt[];
  placements: string[];
  duplicates: string[];
  notReceipts: string[];
  blackAndWhite: string[];
}

export interface ReceiptSummary {
  id: string;
  receiptNumber: string;
  receivedOn: string;
  source: 'ocr' | 'manual';
  status: 'open' | 'closed';
  createdAt: string;
  expected: number;
  processed: number;
}

export interface ItemReport {
  itemNumber: string;
  productId: string | null;
  title: string | null;
  expected: number;
  retail: number;
  auction: number;
  garbage: number;
  processed: number;
  over: number;
  under: number;
  unitRetail: string | null;
  unitCost: string | null;
  lotsListed: number;
}

export interface ManifestReport {
  items: ItemReport[];
  totals: {
    expected: number;
    processed: number;
    retail: number;
    auction: number;
    garbage: number;
    over: number;
    under: number;
    unitPct: Record<Outcome, number>;
    valuePct: Record<Outcome, number>;
    costTotal: string;
    retailTotal: string;
    overValueRetail: string;
    underValueRetail: string;
    underValueCost: string;
    progressPct: number;
    lotsListed: number;
  };
}

export interface ReceiptDetail {
  receipt: { id: string; receiptNumber: string; receivedOn: string; source: string; status: 'open' | 'closed'; notes: string | null; closeNote: string | null; closedAt: string | null };
  report: ManifestReport;
}

export interface ReceiveEvent {
  id: string;
  clientEventId: string;
  itemNumber: string;
  productId: string;
  outcome: Outcome;
  quantity: number;
  packSize: number | null;
  garbageReason: string | null;
  createdAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  replacesEventId: string | null;
  byName: string;
  binCode: string | null;
  binId: string | null;
  title: string | null;
}

export interface ScanResult {
  product: { id: string; itemNumber: string; title: string | null; manifestPrice: string | null; photoId: string | null };
  matchedBy: 'barcode' | 'item_number' | 'photo';
  onManifest: { expected: number; processed: number; retail: number; auction: number; garbage: number } | null;
  price: { price: string; currency: string; source: string; version: number } | null;
  suggestion: { price: string; percent: number } | null;
  packs: { unitsPerItem: number; sellSizes: number[] } | null;
}

export const OUTCOME_LABEL: Record<Outcome, string> = { retail: 'Retail', auction: 'Auction', garbage: 'Garbage' };

/**
 * Breaks `units` into the allowed pack sizes: as few packs as possible, larger packs first, using
 * every unit when the sizes allow it (27 with 6/4/1 -> 6x3, 4x2, 1x1). Returns size -> count and
 * the units that could not be packed.
 */
export function breakup(units: number, sizes: number[]): { packs: { size: number; count: number }[]; leftover: number } {
  const s = [...new Set(sizes)].filter((x) => x > 0).sort((a, b) => b - a);
  // best[n] = fewest packs that make exactly n units (null = impossible).
  const best: (number[] | null)[] = [[]];
  for (let n = 1; n <= units; n++) {
    let pick: number[] | null = null;
    for (const size of s) {
      const prev = size <= n ? best[n - size] : null;
      if (prev && (!pick || prev.length + 1 < pick.length)) pick = [...prev, size];
    }
    best.push(pick);
  }
  let used = units;
  while (used > 0 && !best[used]) used--;
  const counts = new Map<number, number>();
  for (const size of best[used] ?? []) counts.set(size, (counts.get(size) ?? 0) + 1);
  return { packs: s.filter((x) => counts.has(x)).map((size) => ({ size, count: counts.get(size)! })), leftover: units - used };
}
